/**
 * One queue of task writes per mailbox (ADR 0069).
 *
 * Writes go out one at a time, in the order they were made. Two reasons:
 *
 * - **Order is data here.** Quick-add puts each new task at the top, on the
 *   server, in the order requests ARRIVE. Typing "milk ⏎ eggs ⏎ bread ⏎"
 *   fires three creates within a second; sent in parallel they can land in
 *   any order, and the list would reshuffle under the person who just typed
 *   it. Sent in order, the server's answer matches what is already on screen.
 * - **A task made a moment ago has no id yet.** Its row carries a temporary
 *   one, and ticking it off before the create answers must still work. A
 *   queued write resolves its id when it RUNS (`resolveId`), by which time the
 *   create ahead of it has answered and recorded the real one.
 *
 * `isBusy` lets the live-update handler hold back a refetch while writes are
 * still going out: the `tasks_changed` echo of this tab's own first write
 * would otherwise refetch the list and drop the optimistic rows of the writes
 * behind it, and they would flicker out and back in.
 */

interface Lane {
  tail: Promise<unknown>;
  pending: number;
  idle: Array<() => void>;
}

const lanes = new Map<string, Lane>();
const realIds = new Map<string, string>();

function lane(accountId: string): Lane {
  let current = lanes.get(accountId);
  if (!current) {
    current = { tail: Promise.resolve(), pending: 0, idle: [] };
    lanes.set(accountId, current);
  }
  return current;
}

/** Runs `write` after every earlier write for this mailbox has settled. */
export function enqueueTaskWrite<T>(accountId: string, write: () => Promise<T>): Promise<T> {
  const current = lane(accountId);
  current.pending += 1;
  const run = current.tail.then(write, write);
  current.tail = run.then(
    () => settle(current),
    () => settle(current),
  );
  return run;
}

function settle(current: Lane): void {
  current.pending -= 1;
  if (current.pending > 0) return;
  const waiting = current.idle.splice(0);
  for (const callback of waiting) callback();
}

export function isTaskQueueBusy(accountId: string): boolean {
  return (lanes.get(accountId)?.pending ?? 0) > 0;
}

/** Calls `callback` once the mailbox's queue is empty (now, if it already is). */
export function whenTaskQueueIdle(accountId: string, callback: () => void): void {
  const current = lanes.get(accountId);
  if (!current || current.pending === 0) {
    callback();
    return;
  }
  // One pending refetch is enough, however many frames arrive meanwhile.
  if (!current.idle.includes(callback)) current.idle.push(callback);
}

const TEMP_PREFIX = 'pending-';
let tempSeq = 0;

/** A temporary id for a row whose create has not answered yet. */
export function temporaryTaskId(): string {
  tempSeq += 1;
  return `${TEMP_PREFIX}${Date.now().toString(36)}-${tempSeq}`;
}

export function isTemporaryTaskId(id: string): boolean {
  return id.startsWith(TEMP_PREFIX);
}

/** Records the server's id for a temporary one, once its create answers. */
export function recordRealTaskId(temporary: string, real: string): void {
  realIds.set(temporary, real);
}

/** The id to send: the server's, if the row was made here a moment ago. */
export function resolveTaskId(id: string): string {
  return realIds.get(id) ?? id;
}

/** Tests only. */
export function resetTaskQueueForTests(): void {
  lanes.clear();
  realIds.clear();
  tempSeq = 0;
}
